import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, RotateCcw, ShoppingBag } from "lucide-react";
import api from "../lib/api";
import { useSeo } from "../lib/seo";
import ProductCard from "../components/ProductCard";

const STEP_ANIM = {
  initial: { opacity: 0, x: 60 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -60 },
  transition: { duration: 0.35, ease: "easeInOut" },
};

function ProgressBar({ current, total }) {
  return (
    <div className="flex items-center gap-2 mb-8" data-testid="quiz-progress">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-full transition-all duration-500 ${
            i <= current ? "bg-primary" : "bg-border"
          }`}
        />
      ))}
    </div>
  );
}

function QuestionStep({ question, selected, onSelect }) {
  return (
    <div data-testid={`quiz-step-${question.id}`}>
      <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2">
        Question {question.index + 1} of {question.total}
      </p>
      <h2 className="font-display text-2xl sm:text-3xl text-primary mb-8 leading-snug">
        {question.question}
      </h2>
      <div className="grid grid-cols-2 gap-4 sm:gap-5">
        {question.options.map((opt) => {
          const active = selected === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onSelect(opt.id)}
              className={`group relative text-left overflow-hidden rounded transition-all duration-300 outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                active
                  ? "ring-2 ring-primary shadow-lg scale-[1.02]"
                  : "ring-1 ring-border hover:ring-primary/50 hover:shadow-md"
              }`}
              data-testid={`quiz-option-${opt.id}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-muted">
                <img
                  src={opt.image}
                  alt={opt.label}
                  className={`w-full h-full object-cover transition-transform duration-700 ${
                    active ? "scale-105" : "group-hover:scale-105"
                  }`}
                  loading="lazy"
                />
                {active && (
                  <div className="absolute inset-0 bg-primary/15 pointer-events-none" />
                )}
              </div>
              <div className="p-3 sm:p-4">
                <p
                  className={`font-display text-base sm:text-lg leading-snug ${
                    active ? "text-primary" : "text-foreground"
                  }`}
                >
                  {opt.label}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-snug">
                  {opt.desc}
                </p>
              </div>
              {active && (
                <div className="absolute top-2.5 right-2.5 h-6 w-6 bg-primary rounded-full flex items-center justify-center">
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="3">
                    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResultsView({ results, onRetake }) {
  const { profile, products } = results;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="quiz-results"
    >
      {/* Profile card */}
      <div className="text-center mb-14">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="inline-flex items-center gap-2 bg-accent/15 text-accent px-5 py-2 rounded-full text-sm font-semibold mb-5"
        >
          <Sparkles size={16} /> Your Style Profile
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl text-primary mb-4"
          data-testid="quiz-profile-name"
        >
          {profile.name}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed"
          data-testid="quiz-profile-desc"
        >
          {profile.desc}
        </motion.p>
      </div>

      {/* Recommended products */}
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">Curated for you</p>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl text-primary mb-8">
          Pieces that match your style
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-center gap-4 mt-12">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 text-sm tracking-wide hover:gap-3 transition-all"
          data-testid="quiz-shop-btn"
        >
          <ShoppingBag size={18} /> Shop All Products
        </Link>
        <button
          onClick={onRetake}
          className="inline-flex items-center gap-2 border border-primary/30 text-primary px-8 py-4 text-sm tracking-wide hover:bg-secondary transition-colors"
          data-testid="quiz-retake-btn"
        >
          <RotateCcw size={16} /> Retake Quiz
        </button>
      </div>
    </motion.div>
  );
}

export default function Quiz() {
  useSeo({ title: "Style Quiz | Aethel Decor", description: "Discover your decor personality with our visual style quiz and get personalized product recommendations." });

  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/quiz/questions").then((r) => {
      setQuestions(r.data);
      setLoading(false);
    });
  }, []);

  const handleSelect = useCallback(
    (optionId) => {
      const q = questions[step];
      setAnswers((prev) => ({ ...prev, [q.id]: optionId }));
    },
    [questions, step]
  );

  const handleNext = useCallback(() => {
    if (step < questions.length - 1) {
      setStep((s) => s + 1);
    }
  }, [step, questions.length]);

  const handleBack = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/quiz/results", { answers });
      setResults(r.data);
    } catch {
      // fallback — show generic error
    } finally {
      setSubmitting(false);
    }
  }, [answers]);

  const handleRetake = useCallback(() => {
    setStep(0);
    setAnswers({});
    setResults(null);
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground" data-testid="quiz-loading">
        Loading quiz...
      </div>
    );
  }

  const currentQ = questions[step];
  const currentAnswer = currentQ ? answers[currentQ.id] : null;
  const isLast = step === questions.length - 1;
  const allAnswered = questions.every((q) => answers[q.id]);

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-20" data-testid="quiz-page">
      {results ? (
        <ResultsView results={results} onRetake={handleRetake} />
      ) : (
        <>
          {/* Header */}
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">Style Quiz</p>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl text-primary">
              Discover your decor personality
            </h1>
            <p className="mt-3 text-muted-foreground text-sm sm:text-base max-w-md mx-auto">
              Answer 5 quick questions and we'll curate a collection just for you.
            </p>
          </div>

          <ProgressBar current={step} total={questions.length} />

          {/* Question */}
          <AnimatePresence mode="wait">
            <motion.div key={currentQ.id} {...STEP_ANIM}>
              <QuestionStep
                question={{ ...currentQ, index: step, total: questions.length }}
                selected={currentAnswer}
                onSelect={handleSelect}
              />
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10">
            <button
              onClick={handleBack}
              disabled={step === 0}
              className="inline-flex items-center gap-2 text-sm text-primary disabled:opacity-30 disabled:cursor-not-allowed hover:gap-1.5 transition-all"
              data-testid="quiz-back-btn"
            >
              <ArrowLeft size={16} /> Back
            </button>

            {isLast ? (
              <button
                onClick={handleSubmit}
                disabled={!allAnswered || submitting}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 text-sm tracking-wide disabled:opacity-50 disabled:cursor-not-allowed hover:gap-3 transition-all"
                data-testid="quiz-submit-btn"
              >
                {submitting ? "Finding your style..." : "See My Results"}
                <Sparkles size={16} />
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!currentAnswer}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 text-sm tracking-wide disabled:opacity-50 disabled:cursor-not-allowed hover:gap-3 transition-all"
                data-testid="quiz-next-btn"
              >
                Next <ArrowRight size={16} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
